/**
 * Exportação "página no servidor" — separada do ZIP do Moodle.
 * Nada daqui roda no export Moodle, no preview nem no plugin.
 */

export const SITE_EXPORT_RUNTIME = 'site';

export function isSiteExportRuntime(exportOptions) {
  return !!(exportOptions && exportOptions.runtime === SITE_EXPORT_RUNTIME);
}

function escHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** JSON seguro dentro de <script>. */
function jsEmbed(value) {
  return JSON.stringify(value == null ? '' : String(value)).replace(/</g, '\\u003c');
}

const MOODLE_PATH_RE = /(?:^|\/)(?:mod|course)\//i;
const MOODLE_FILE_RE = /pluginfile\.php|draftfile\.php|\/mod\/h5p|\/mod\/hvp|\/h5p\/embed/i;

export function isMoodleBoundHref(link) {
  const raw = String(link ?? '').trim();
  if (!raw || raw === '#' || raw.charAt(0) === '#') return false;
  if (raw.slice(0, 2) === '//') return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw) && !/^https?:/i.test(raw)) return false;
  let path = raw;
  if (/^https?:/i.test(raw)) {
    try {
      path = new URL(raw).pathname || '';
    } catch (_) {
      return false;
    }
  }
  return MOODLE_PATH_RE.test(path) || MOODLE_FILE_RE.test(path);
}

export function looksLikeMoodleBoundEmbed(url, embedCode) {
  const blob = `${url || ''}\n${embedCode || ''}`;
  if (!String(blob).trim()) return false;
  return MOODLE_FILE_RE.test(blob) || MOODLE_PATH_RE.test(blob);
}

export function buildSiteOmittedNoticeHtml(message) {
  return `<div class="geppetto-site-omitted" role="note" style="padding:14px 16px;border:1px dashed #cbd5e1;border-radius:8px;background:#f8fafc;color:#475569;font-size:14px;line-height:1.5">${escHtml(message)}</div>`;
}

function bump(map, id) {
  map[id] = (map[id] || 0) + 1;
}

function mapToList(map) {
  return Object.keys(map).map((id) => ({ id, count: map[id] }));
}

function walkColumns(columns, visit) {
  (columns || []).forEach((col) => {
    (col.widgets || []).forEach((w) => visit(w));
    if (Array.isArray(col.nestedRows)) {
      col.nestedRows.forEach((row) => walkColumns(row.columns, visit));
    }
  });
}

/**
 * Varre o layout para o aviso do modal. Não altera o HTML.
 */
export function collectSiteExportAudit(rows, headerWidgets, globalConfig) {
  const local = {};
  const omitted = {};
  const limited = {};
  let moodleLinks = 0;

  const visit = (w) => {
    if (!w || !w.type) return;
    const c = w.content || {};
    if (w.type === 'comments') bump(local, 'comments');
    if (w.type === 'reinforcement-quiz') bump(local, 'microquiz');
    if (w.type === 'checkpoint' || c.checkpoint?.enabled) bump(local, 'checkpoint');
    if (w.type === 'progress-bar') bump(local, 'progress');
    if (w.type === 'moodle-activity') bump(omitted, 'moodleActivity');
    if (w.type === 'moodle-quiz') bump(omitted, 'moodleQuiz');
    if (w.type === 'moodle-forum') bump(omitted, 'moodleForum');
    if (w.type === 'asaas-payment') bump(omitted, 'asaas');
    if (w.type === 'mercadopago-payment') bump(omitted, 'mercadopago');
    if (w.type === 'h5p' && looksLikeMoodleBoundEmbed(c.url, c.embedCode)) bump(omitted, 'h5p');
    if (w.type === 'htmlTemplate' && looksLikeMoodleBoundEmbed('', c.html)) bump(limited, 'htmlTemplate');
    if (w.type === 'styled-button' && isMoodleBoundHref(c.link)) moodleLinks += 1;
    if (w.type === 'image-card' && c.showBtn !== false && isMoodleBoundHref(c.btnLink)) moodleLinks += 1;
    if (w.type === 'social-card' && c.showBtn !== false && isMoodleBoundHref(c.btnLink)) moodleLinks += 1;
    if (w.type === 'header-menu' && Array.isArray(c.items)) {
      c.items.forEach((it) => {
        if (isMoodleBoundHref(it && it.href)) moodleLinks += 1;
      });
    }
  };

  (headerWidgets || []).forEach(visit);
  (rows || []).forEach((row) => walkColumns(row.columns, visit));

  const fm = globalConfig && globalConfig.floatingMenu;
  if (fm && fm.enabled && Array.isArray(fm.items)) {
    fm.items.forEach((it) => {
      if (isMoodleBoundHref(it && it.url)) moodleLinks += 1;
    });
  }

  return {
    local: mapToList(local),
    omitted: mapToList(omitted),
    limited: mapToList(limited),
    moodleLinks,
  };
}

export function finalizeSiteExportHtml(html) {
  return String(html || '')
    .split('[[GEPPETTO_CMID]]').join('')
    .split('[[GEPPETTO_SESSKEY]]').join('');
}

/**
 * Scripts exclusivos da página publicada: progresso e comentários no navegador.
 * O link de uma atividade segue o endereço. Quem pode ver o recurso, o Moodle abre;
 * quem não pode, o próprio Moodle recusa. Não entra no ZIP do curso.
 */
export function buildSiteRuntimeBundle({ wrapperId, hasComments, strings = {} }) {
  const wId = jsEmbed(wrapperId || 'course');
  const commentName = jsEmbed(strings.commentName || 'Seu nome');
  const commentPlaceholder = jsEmbed(strings.commentPlaceholder || 'Escreva um comentário');
  const commentSend = jsEmbed(strings.commentSend || 'Publicar');
  const commentEmpty = jsEmbed(strings.commentEmpty || 'Nenhum comentário ainda.');
  const commentsBoot = hasComments ? 'bootComments();' : '';

  return `<script data-geppetto-site-runtime="1">
(function(){
  var storageId = ${wId};

  function ckKey() { return 'geppetto-site-ck:' + storageId; }
  function readCk() {
    try {
      var parsed = JSON.parse(localStorage.getItem(ckKey()) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }
  function writeCk(arr) {
    try { localStorage.setItem(ckKey(), JSON.stringify(arr)); } catch (e) {}
  }
  function applyCk() {
    var api = window.__geppettoCkProgress;
    if (!api || typeof api.mergeDone !== 'function') return false;
    api.mergeDone(readCk());
    return true;
  }
  if (!applyCk()) {
    var tries = 0;
    var timer = setInterval(function() {
      tries += 1;
      if (applyCk() || tries > 20) clearInterval(timer);
    }, 50);
  }
  document.addEventListener('geppettoCheckpointDone', function(ev) {
    if (ev && ev.detail && ev.detail.source === 'sync') return;
    var api = window.__geppettoCkProgress;
    if (api && typeof api.getDone === 'function') writeCk(api.getDone());
  });

  function bootComments() {
    var nameKey = 'geppetto-site-commenter';
    var labelName = ${commentName};
    var labelPlaceholder = ${commentPlaceholder};
    var labelSend = ${commentSend};
    var labelEmpty = ${commentEmpty};
    var blocks = document.querySelectorAll('.madri-geppetto-comments');
    for (var i = 0; i < blocks.length; i++) initComments(blocks[i]);

    function storeKey(el) {
      return 'geppetto-site-comments:' + storageId + ':' + String(el.getAttribute('data-element-key') || 'comments');
    }
    function readList(el) {
      try {
        var parsed = JSON.parse(localStorage.getItem(storeKey(el)) || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) { return []; }
    }
    function writeList(el, list) {
      try { localStorage.setItem(storeKey(el), JSON.stringify(list)); } catch (e) {}
    }
    function esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
    function initComments(root) {
      var composer = root.querySelector('.madri-gc-composer');
      var listEl = root.querySelector('.madri-gc-list');
      if (!composer || !listEl) return;
      var savedName = '';
      try { savedName = localStorage.getItem(nameKey) || ''; } catch (e) {}
      composer.innerHTML = '<input class="geppetto-site-comment-name" type="text" maxlength="80" placeholder="' + esc(labelName) + '" value="' + esc(savedName) + '" style="width:100%;box-sizing:border-box;margin-bottom:8px;padding:8px 10px;border:1px solid #e2e8f0;border-radius:8px;font:inherit">'
        + '<textarea class="geppetto-site-comment-text" maxlength="2000" placeholder="' + esc(labelPlaceholder) + '" style="width:100%;box-sizing:border-box;min-height:72px;margin-bottom:8px;padding:8px 10px;border:1px solid #e2e8f0;border-radius:8px;font:inherit"></textarea>'
        + '<button type="button" class="geppetto-site-comment-send" style="background:#3b82f6;color:#fff;border:none;border-radius:8px;padding:8px 16px;font-weight:600;cursor:pointer">' + esc(labelSend) + '</button>';
      function paint() {
        var list = readList(root);
        if (!list.length) {
          listEl.innerHTML = '<p style="color:#64748b;font-size:14px;margin:12px 0 0">' + esc(labelEmpty) + '</p>';
          return;
        }
        listEl.innerHTML = list.map(function(item) {
          var when = '';
          try { when = new Date(item.at).toLocaleString(); } catch (e2) {}
          return '<div style="margin-top:12px;padding:12px;border:1px solid #e2e8f0;border-radius:10px;background:#fff"><strong style="font-size:14px">' + esc(item.name || labelName) + '</strong>'
            + '<span style="float:right;color:#94a3b8;font-size:12px">' + esc(when) + '</span>'
            + '<p style="margin:8px 0 0;white-space:pre-wrap;font-size:14px;line-height:1.5;color:#334155">' + esc(item.text) + '</p></div>';
        }).join('');
      }
      composer.querySelector('.geppetto-site-comment-send').addEventListener('click', function() {
        var nameInput = composer.querySelector('.geppetto-site-comment-name');
        var textInput = composer.querySelector('.geppetto-site-comment-text');
        var name = String(nameInput.value || '').trim().slice(0, 80);
        var text = String(textInput.value || '').trim().slice(0, 2000);
        if (!text) return;
        if (!name) name = labelName;
        try { localStorage.setItem(nameKey, name); } catch (e3) {}
        var list = readList(root);
        list.unshift({ name: name, text: text, at: Date.now() });
        writeList(root, list.slice(0, 100));
        textInput.value = '';
        paint();
      });
      paint();
    }
  }
  ${commentsBoot}
})();
</script>`;
}

export function resolveSitePublishApiBase(apiBase) {
  const raw = String(apiBase || '').trim().replace(/\/$/, '');
  if (/^https?:\/\//i.test(raw)) return raw;
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const path = raw.startsWith('/') ? raw : `/${raw || 'api'}`;
    return `${window.location.origin}${path}`.replace(/\/$/, '');
  }
  return raw;
}

/** Esconde a cópia gravada no index.html até a versão publicada chegar. Sem isso o navegador pinta o ZIP antigo e só depois troca. */
const SITE_SYNC_HOLD_HEAD = `<style id="geppetto-site-hold">body{display:none!important}html::before{content:"";position:fixed;top:50%;left:50%;width:28px;height:28px;margin:-14px 0 0 -14px;border:3px solid #e2e8f0;border-top-color:#417cd5;border-radius:50%;animation:geppettoSiteSpin .7s linear infinite}@keyframes geppettoSiteSpin{to{transform:rotate(360deg)}}</style>
<noscript><style>body{display:block!important}html::before{display:none!important}</style></noscript>`;

export function buildSiteSyncScript({ apiBase, token }) {
  const api = jsEmbed(String(apiBase || '').replace(/\/$/, ''));
  const tok = jsEmbed(token || '');
  return `<script data-geppetto-site-sync="1">
(function(){
  var api = ${api};
  var token = ${tok};
  var settled = false;
  function showBaked() {
    if (settled) return;
    settled = true;
    var hold = document.getElementById('geppetto-site-hold');
    if (hold && hold.parentNode) hold.parentNode.removeChild(hold);
  }
  if (!api || !token) { showBaked(); return; }
  var timer = setTimeout(showBaked, 12000);
  fetch(api + '/site-publish/' + encodeURIComponent(token), { cache: 'no-store' })
    .then(function(r){ if (!r.ok) throw new Error('no'); return r.json(); })
    .then(function(data){
      var html = data && data.html ? String(data.html) : '';
      if (!html || html.indexOf('data-geppetto-site-sync') !== -1) { showBaked(); return; }
      if (html.indexOf('<html') === -1 && html.indexOf('<HTML') === -1) { showBaked(); return; }
      settled = true;
      clearTimeout(timer);
      document.open();
      document.write(html);
      document.close();
    })
    .catch(function(){ showBaked(); });
})();
</script>`;
}

export function injectSiteSyncScript(documentHtml, script) {
  let html = String(documentHtml || '');
  const marker = String(script || '');
  if (!marker) return html;
  if (html.includes('</head>')) html = html.replace('</head>', `${SITE_SYNC_HOLD_HEAD}\n</head>`);
  else html = `${SITE_SYNC_HOLD_HEAD}\n${html}`;
  if (html.includes('</body>')) return html.replace('</body>', `${marker}\n</body>`);
  return html + marker;
}

export function buildSiteIndexDocument({ title, lang, bodyHtml, css }) {
  const safeTitle = escHtml(title || 'Geppetto');
  const htmlLang = lang === 'en' ? 'en' : 'pt-BR';
  const safeCss = String(css || '').replace(/<\/style>/gi, '\\3C \\2F style \\3E');
  return `<!DOCTYPE html>
<html lang="${htmlLang}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta name="generator" content="Geppetto Site Export">
  <title>${safeTitle}</title>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css">
  <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
  <style>html,body{width:100%;margin:0;padding:0;background:#ffffff;box-sizing:border-box}*,*::before,*::after{box-sizing:border-box}</style>
  <style>${safeCss}</style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
}

export function buildSiteReadme(strings = {}) {
  return strings.readme || `PÁGINA GERADA PELO GEPPETTO
===========================

1. Extraia esta pasta.
2. Envie index.html e style.css juntos para uma pasta do servidor.
3. Abra a URL dessa pasta no navegador.

Quizzes, checkpoints, barra de progresso e comentários ficam gravados neste navegador.
Atividades do Moodle, H5P interno do curso e pagamentos Asaas/Mercado Pago não funcionam nesta página.
`;
}
